import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '../hooks';
import { queries } from '../queries';
import { EmptyState, StatusBadge } from '../components/ui';
import { deploymentModeLabel, humanize, plural } from '../labels';
import { Callout, Icon, Page, PageError, PageHeader, PageLoading, Table, clickableRow } from '../components/primitives';
import { HelpTip } from '../components/primitives/HelpTip';
import { Fold, ResponsiveTable } from '../components/primitives/ResponsiveTable';
import type { Column } from '../components/primitives/ResponsiveTable';
import { PortfolioFeedSection, SitesOverview, serverContactLine } from '../components/portfolio/PortfolioStatus';
import { OutcomeBadge, Timestamp, WhenCell } from '../components/status/StatusPieces';
import { inProgress, outcomeOfStatus } from '../components/status/statusModel';
import type { ProjectDeployment } from '../api';

interface RecentRow {
  deployment: ProjectDeployment;
  project_name: string;
  environment_name: string;
  environment_kind: string;
}

const recentColumns: Column<RecentRow>[] = [
  {
    id: 'project',
    header: 'Project',
    card: 'title',
    width: 14,
    cell: (row) => (
      <span className="cell-stack">
        <span className="cell-stack__primary">{row.project_name || row.deployment.project_id.slice(0, 8)}</span>
        <span className="cell-stack__secondary">{row.environment_name || '—'}</span>
      </span>
    ),
  },
  {
    id: 'component',
    header: 'Component',
    priority: 'secondary',
    width: 16,
    cell: (row) => row.deployment.component_name || <span className="muted">Not recorded</span>,
  },
  {
    id: 'change',
    header: 'Change',
    width: 'fill',
    cell: (row) => (
      <span className="cell-stack">
        {/* In a narrow table the Component column gives way; its name stays here. */}
        <Fold>
          <span className="cell-stack__secondary">{row.deployment.component_name || 'Component not recorded'}</span>
        </Fold>
        {row.deployment.source_commit_message ? (
          <>
            <span className="cell-stack__primary cell-clamp">{row.deployment.source_commit_message.split('\n')[0]}</span>
            <span className="cell-stack__secondary">
              <code translate="no">{row.deployment.source_commit_sha.slice(0, 7)}</code>
              {row.deployment.source_branch ? <> on {row.deployment.source_branch}</> : null}
            </span>
          </>
        ) : (
          // While the message is not known the commit names the change (CO-152).
          <span className="cell-stack__primary">
            Commit <code translate="no">{row.deployment.source_commit_sha.slice(0, 7)}</code>
            {row.deployment.source_branch ? <> on {row.deployment.source_branch}</> : null}
          </span>
        )}
      </span>
    ),
  },
  {
    id: 'outcome',
    header: 'Outcome',
    card: 'status',
    width: 'status',
    cell: (row) => <OutcomeBadge outcome={outcomeOfStatus(row.deployment.status, row.deployment.failure_reason)} />,
  },
  {
    id: 'when',
    header: 'Started',
    width: 'time',
    cell: (row) => (
      <WhenCell
        created={row.deployment.created_at}
        started={row.deployment.started_at}
        completed={row.deployment.completed_at}
        running={inProgress(outcomeOfStatus(row.deployment.status, row.deployment.failure_reason))}
        waiting={outcomeOfStatus(row.deployment.status, row.deployment.failure_reason) === 'queued'}
      />
    ),
  },
];

/**
 * The Dashboard (CO-150): every store's live site first — what visitors get
 * now — then genuine live-site incidents, then failed or partial updates in
 * their own compact list, then infrastructure and everything else, then
 * what was deployed recently. A failed update that left the previous
 * release serving never turns a project into a red incident here.
 */
/** Recent deployments shown before "Show all" (CO-161: history stays secondary). */
const RECENT_SHOWN = 5;

export function OverviewPage() {
  const navigate = useNavigate();
  const overview = useQuery(queries.overview());
  const health = useQuery(queries.organizationHealth());
  const [allRecent, setAllRecent] = useState(false);

  // The page keeps its header while it loads and when it cannot be read (CO-184, OR3).
  if (overview.loading) return <PageLoading title="Dashboard" rows={6} />;
  if (overview.error) return <PageError title="Dashboard" error={overview.error} onRetry={overview.reload} />;
  const data = overview.data;
  if (!data) return <PageLoading title="Dashboard" rows={6} />;

  if (data.project_count === 0) {
    return (
      <Page>
        <PageHeader title="Dashboard" />
        <EmptyState title="No projects" description="">
          <div className="inline-form">
            <Link className="button" to="/app/projects/new">
              New project
            </Link>
            <Link className="button button--secondary" to="/app/servers/new">
              Connect a server
            </Link>
          </div>
        </EmptyState>
      </Page>
    );
  }

  const legacyAttempts = data.active_deployments;
  const recent = (data.recent_project_deployments ?? []) as RecentRow[];
  const inProgressCount = (data.project_deployments_in_progress ?? 0) + legacyAttempts;
  const contact = serverContactLine(health.data);

  return (
    <Page className="dashboard">
      <PageHeader
        title="Dashboard"
        actions={
          <Link to="/app/projects/new" className="button button--primary">
            <Icon name="plus" />
            New project
          </Link>
        }
      />

      {data.needing_recovery > 0 ? (
        <Callout tone="danger" title={`${plural(data.needing_recovery, 'deployment')} stuck part-way`}>
          Open the environment to finish or undo it.
        </Callout>
      ) : null}

      <section className="dash-section" aria-labelledby="dash-sites-title" data-testid="dash-sites">
        <div className="dash-section__head">
          <h2 id="dash-sites-title" className="dash-section__title">
            Live sites
            <HelpTip label="Where a site's state comes from">A site's state comes from its own checks, not from its deployments.</HelpTip>
          </h2>
        </div>
        <SitesOverview health={health.data} loading={health.loading} error={health.error} onRetry={health.reload} />
      </section>

      <PortfolioFeedSection
        health={health.data}
        loading={health.loading}
        error={health.error}
        noun="incidents"
        title="Live-site incidents"
        categories={['site']}
        testId="dash-incidents"
      />
      <PortfolioFeedSection
        health={health.data}
        loading={health.loading}
        error={health.error}
        noun="failed updates"
        title="Deployment updates"
        categories={['deployment']}
        testId="dash-deployments"
      />
      <PortfolioFeedSection
        health={health.data}
        loading={health.loading}
        error={health.error}
        noun="warnings"
        title="Infrastructure and operations"
        note={contact}
        categories={['infrastructure', 'recovery', 'security', 'setup', 'operations']}
        testId="dash-infrastructure"
      />

      <section className="dash-section" aria-labelledby="dash-recent-title" data-testid="dash-recent">
        <div className="dash-section__head">
          <h2 id="dash-recent-title" className="dash-section__title">
            Recent deployments
          </h2>
          {inProgressCount > 0 ? <p className="dash-section__desc">{inProgressCount} running</p> : null}
        </div>
        {recent.length === 0 && data.recent_deployments.length === 0 ? (
          <p className="quiet-empty">No deployments</p>
        ) : null}
        {recent.length > 0 ? (
          <ResponsiveTable
            rows={allRecent ? recent : recent.slice(0, RECENT_SHOWN)}
            columns={recentColumns}
            rowKey={(row) => row.deployment.id}
            caption="Recent deployments, newest first"
            testId="recent-deployments-table"
            rowLink={(row) => ({
              to: `/app/projects/${row.deployment.project_id}/deployments?deployment=${row.deployment.id}`,
              accessibleName: `Open the ${row.deployment.source_commit_sha.slice(0, 7)} deployment of ${row.project_name}`,
            })}
          />
        ) : null}
        {recent.length > RECENT_SHOWN ? (
          <button type="button" className="link-button dash-section__more" aria-expanded={allRecent} onClick={() => setAllRecent((value) => !value)} data-testid="dash-recent-toggle">
            {allRecent ? 'Show fewer' : `Show ${recent.length - RECENT_SHOWN} more`}
          </button>
        ) : null}
      </section>

      {data.recent_deployments.length > 0 || Object.keys(data.mode_distribution).length > 0 ? (
        <details className="quiet-disclosure dash-paths" data-testid="dash-deployment-paths">
          <summary>
            {data.recent_deployments.length > 0
              ? `SSH-deployed environments: ${plural(data.recent_deployments.length, 'recent attempt')}`
              : 'Environments by deployment path'}
          </summary>
          {Object.keys(data.mode_distribution).length > 0 ? (
            <p className="dash-section__desc">
              {Object.entries(data.mode_distribution)
                .map(([mode, count]) => `${deploymentModeLabel[mode as keyof typeof deploymentModeLabel] ?? humanize(mode)}: ${count}`)
                .join(' · ')}
            </p>
          ) : null}
          {data.recent_deployments.length > 0 ? (
            <Table label={`Attempts on environments of type “${deploymentModeLabel.existing_atomic}”`}>
              <thead>
                <tr>
                  <th scope="col">Status</th>
                  <th scope="col">Commit</th>
                  <th scope="col">Attempt</th>
                  <th scope="col">Started</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_deployments.map(({ deployment, release }) => (
                  <tr key={deployment.id} {...clickableRow(() => navigate(`/app/environments/${deployment.environment_id}`))}>
                    <td>
                      <StatusBadge kind="legacyDeployment" value={deployment.status} />
                    </td>
                    <td>
                      <Link to={`/app/environments/${deployment.environment_id}`} aria-label={`Open the environment of attempt ${deployment.attempt_number}`}>
                        <code>{release.commit_sha.slice(0, 12)}</code>
                      </Link>
                    </td>
                    <td>#{deployment.attempt_number}</td>
                    <td>
                      <Timestamp iso={deployment.created_at} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </details>
      ) : null}
    </Page>
  );
}
